import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BusinessDomain,
  BUSINESS_DOMAIN_STORAGE_KEY,
  DOMAIN_DEFAULT_ROUTES,
  domainForPath,
  navItemsForDomain,
} from '../config/BusinessDomainConfig';

interface BusinessDomainContextValue {
  domain: BusinessDomain;
  setDomain: (domain: BusinessDomain) => void;
  isRouteInCurrentDomain: (pathname: string) => boolean;
}

const BusinessDomainContext = createContext<BusinessDomainContextValue | undefined>(undefined);

function readStoredDomain(): BusinessDomain | null {
  try {
    const raw = localStorage.getItem(BUSINESS_DOMAIN_STORAGE_KEY);
    if (raw === 'LENDING' || raw === 'CAPITALS') {
      return raw;
    }
  } catch {
  }
  return null;
}

export function BusinessDomainProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [domain, setDomainState] = useState<BusinessDomain>(() => {
    return readStoredDomain() ?? domainForPath(window.location.pathname);
  });

  useEffect(() => {
    setDomainState((current) => {
      const pathDomain = domainForPath(location.pathname);
      if (pathDomain === current) {
        return current;
      }
      try {
        localStorage.setItem(BUSINESS_DOMAIN_STORAGE_KEY, pathDomain);
      } catch {
      }
      return pathDomain;
    });
  }, [location.pathname]);

  const setDomain = useCallback((next: BusinessDomain) => {
    setDomainState(next);
    try {
      localStorage.setItem(BUSINESS_DOMAIN_STORAGE_KEY, next);
    } catch {
    }

    const allowed = navItemsForDomain(next).some((item) => item.id === location.pathname);
    if (!allowed) {
      navigate(DOMAIN_DEFAULT_ROUTES[next]);
    }
  }, [location.pathname, navigate]);

  const isRouteInCurrentDomain = useCallback((pathname: string) => {
    return navItemsForDomain(domain).some((item) => item.id === pathname);
  }, [domain]);

  const value = useMemo(() => ({
    domain,
    setDomain,
    isRouteInCurrentDomain,
  }), [domain, setDomain, isRouteInCurrentDomain]);

  return (
    <BusinessDomainContext.Provider value={value}>
      {children}
    </BusinessDomainContext.Provider>
  );
}

export function useBusinessDomain(): BusinessDomainContextValue {
  const ctx = useContext(BusinessDomainContext);
  if (!ctx) {
    throw new Error('useBusinessDomain must be used within BusinessDomainProvider');
  }
  return ctx;
}
